import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Mail, RotateCcw, Upload } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api/admin/email-footer`;

const Field = ({ id, label, ...props }) => (
  <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label><Input id={id} data-testid={id} {...props} /></div>
);

const OfficeFields = ({ prefix, title, hint, office, onChange }) => (
  <div className="space-y-3">
    <div><div className="font-medium text-sm">{title}</div>{hint && <p className="text-xs text-muted-foreground">{hint}</p>}</div>
    <Field id={`${prefix}-address-input`} label="Address" maxLength={200} value={office.address} onChange={(e) => onChange({ ...office, address: e.target.value })} />
    <Field id={`${prefix}-phone-input`} label="Phone" maxLength={40} value={office.phone} onChange={(e) => onChange({ ...office, phone: e.target.value })} />
  </div>
);

const Preview = ({ data, state }) => (
  <div className="dark-keep rounded-lg border bg-white p-4" data-testid={`email-footer-preview-${state}`}
    dangerouslySetInnerHTML={{ __html: data.preview_html[state].replace('{{LOGO}}', `${BASE}${data.logo_preview_url}`) }} />
);

export default function EmailFooterPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const isAdmin = user?.role === 'system_admin';

  const apply = (d) => { setData(d); setForm({ enabled: d.enabled, organization_name: d.organization_name, website: d.website, default_office: d.default_office, nc_office: d.nc_office }); };
  useEffect(() => { axios.get(API).then((r) => apply(r.data)).catch(() => toast.error('Failed to load the email footer settings')); }, []);

  const run = async (fn, ok) => {
    setSaving(true);
    try { apply((await fn()).data); toast.success(ok); }
    catch (e) { toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Please check the footer details'); }
    finally { setSaving(false); }
  };
  const save = () => run(() => axios.put(API, form), 'Email footer saved');
  const upload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    run(() => axios.post(`${API}/logo`, fd), 'Logo updated');
  };

  if (!isAdmin) return <p className="text-muted-foreground" data-testid="email-footer-no-access">Only System Administrators can manage the email footer.</p>;
  if (!form) return <Loader2 className="h-5 w-5 animate-spin" />;
  return (
    <div className="space-y-6 max-w-4xl" data-testid="email-footer-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Mail className="h-6 w-6" />Email Footer</h1>
        <p className="text-muted-foreground text-sm">Added to the bottom of every email MyHELP and HELPConnect send. North Carolina participants see the North Carolina office; everyone else, including staff, sees the main office.</p>
      </div>
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
          <div><CardTitle className="text-base">Footer details</CardTitle><CardDescription>Changes apply to emails sent after you save.</CardDescription></div>
          <div className="flex items-center gap-2"><Label htmlFor="email-footer-enabled">Show footer</Label>
            <Switch id="email-footer-enabled" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="email-footer-enabled-switch" /></div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="email-footer-org-input" label="Organization name" maxLength={120} value={form.organization_name} onChange={(e) => setForm({ ...form, organization_name: e.target.value })} />
            <Field id="email-footer-website-input" label="Website" maxLength={120} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            <OfficeFields prefix="email-footer-main" title="Main office (Tennessee and everyone else)" office={form.default_office} onChange={(o) => setForm({ ...form, default_office: o })} />
            <OfficeFields prefix="email-footer-nc" title="North Carolina office" hint="Leave a field empty to use the main office's details." office={form.nc_office} onChange={(o) => setForm({ ...form, nc_office: o })} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={upload} data-testid="email-footer-logo-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={saving} data-testid="email-footer-logo-upload-btn"><Upload className="h-4 w-4 mr-2" />Replace logo</Button>
            {data.has_custom_logo && <Button variant="ghost" onClick={() => run(() => axios.delete(`${API}/logo`), 'Logo reset')} disabled={saving} data-testid="email-footer-logo-reset-btn"><RotateCcw className="h-4 w-4 mr-2" />Use original logo</Button>}
            <div className="flex-1" />
            <Button onClick={save} disabled={saving || !form.organization_name.trim()} data-testid="email-footer-save-btn">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save</Button>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Preview (saved version)</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5"><div className="text-xs font-medium text-muted-foreground">Main office</div><Preview data={data} state="default" /></div>
          <div className="space-y-1.5"><div className="text-xs font-medium text-muted-foreground">North Carolina participants</div><Preview data={data} state="NC" /></div>
        </CardContent>
      </Card>
    </div>
  );
}
